import { useMemo, useState } from "react";
import { FileText, Printer, Lock, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { parseTourDates, isFutureRequest } from "@/lib/fields";
import { printOrSavePdf } from "@/lib/printReport";

const COUNTED = new Set(["Complete", "Current", "Past (Update)"]);
const TIP_TYPES = ["Cash", "Electronic", "Check", "Mix"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const isoToDate = (s) => (s ? new Date(`${s}T00:00:00`) : null);

// First tour date -> JS Date (income attributed to when the tour happened).
const tourDate = (t) => {
  const raw = Array.isArray(t.tour_date) ? t.tour_date[0] : t.tour_date;
  const m = String(raw || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return null;
  let y = +m[3];
  if (y < 100) y += 2000;
  return new Date(y, +m[1] - 1, +m[2]);
};

export const TaxSummary = ({ tours = [] }) => {
  const { financialPrivacy } = usePrivacy();
  const now = new Date();
  const years = useMemo(() => {
    const set = new Set([now.getFullYear()]);
    tours.forEach((t) => { const d = tourDate(t); if (d) set.add(d.getFullYear()); });
    return [...set].sort((a, b) => b - a);
  }, [tours]); // eslint-disable-line react-hooks/exhaustive-deps

  const [mode, setMode] = useState("year");
  const [year, setYear] = useState(now.getFullYear());
  const [fromIso, setFromIso] = useState("");
  const [toIso, setToIso] = useState("");

  const range = useMemo(() => {
    if (mode === "ytd") return { from: new Date(now.getFullYear(), 0, 1), to: now, label: `${now.getFullYear()} year-to-date` };
    if (mode === "custom") {
      const f = isoToDate(fromIso), t = isoToDate(toIso);
      return { from: f, to: t, label: f && t ? `${fromIso} → ${toIso}` : "Pick a start and end date" };
    }
    return { from: new Date(year, 0, 1), to: new Date(year, 11, 31, 23, 59, 59), label: `Tax year ${year}` };
  }, [mode, year, fromIso, toIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    const { from, to } = range;
    const byType = { Cash: 0, Electronic: 0, Check: 0, Mix: 0, Unspecified: 0 };
    const months = {};
    let count = 0, days = 0, retail = 0, tips = 0, claimed = 0;
    if (from && to) {
      tours.forEach((t) => {
        // A "Mix" tip split into Cash + Electronic uses the split (not the top-line Mix amount).
        const cashAmt = Number(t.tip_cash) || 0;
        const elecAmt = Number(t.tip_electronic) || 0;
        const isMixSplit = t.tip_type === "Mix" && (cashAmt > 0 || elecAmt > 0);
        const g = isMixSplit ? (cashAmt + elecAmt) : (Number(t.gratuity) || 0);
        // Match the Insights stats strip exactly: every non-future-request tour with a date in range,
        // any status. (Tips still only add where a gratuity was recorded.)
        if (isFutureRequest(t)) return;
        const ds = parseTourDates(t.tour_date);
        if (!ds.length || !ds.some((d) => d >= from && d <= to)) return;
        const d = tourDate(t) || ds[0];
        count += 1;
        const dd = typeof t.days === "number" && t.days ? t.days : 1;
        days += dd;
        retail += Number(t.retail_value) || 0;
        const mk = `${d.getFullYear()}-${d.getMonth()}`;
        if (!months[mk]) months[mk] = { y: d.getFullYear(), m: d.getMonth(), tours: 0, days: 0, tips: 0, claimed: 0 };
        months[mk].tours += 1; months[mk].days += dd;
        if (g > 0) {
          tips += g;
          const cl = t.gratuity_claimed && Number(t.gratuity_claimed_amount) > 0 ? Number(t.gratuity_claimed_amount) : 0;
          claimed += cl;
          if (isMixSplit) {
            byType.Cash += cashAmt;
            byType.Electronic += elecAmt;
          } else {
            const type = TIP_TYPES.includes(t.tip_type) ? t.tip_type : "Unspecified";
            byType[type] += g;
          }
          months[mk].tips += g; months[mk].claimed += cl;
        }
      });
    }
    const monthRows = Object.values(months).sort((a, b) => a.y - b.y || a.m - b.m);
    return { count, days, retail, tips, claimed, byType, monthRows, avg: retail > 0 ? (tips / retail) * 100 : 0 };
  }, [tours, range]);

  const [printHtml, setPrintHtml] = useState(null);
  const buildReport = () => {
    const { label } = range;
    const rows = summary.monthRows.map((r) =>
      `<tr><td>${MONTHS[r.m]} ${r.y}</td><td>${r.tours}</td><td>${r.days}</td><td>$${Math.round(r.tips).toLocaleString()}</td><td>$${Math.round(r.claimed).toLocaleString()}</td></tr>`
    ).join("");
    const typeRows = ["Cash", "Electronic", "Check", "Mix", "Unspecified"]
      .filter((k) => summary.byType[k] > 0)
      .map((k) => `<tr><td>${k}</td><td>$${Math.round(summary.byType[k]).toLocaleString()}</td></tr>`).join("");
    setPrintHtml(`
      <style>#tax-print-body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;max-width:720px;margin:auto}
      #tax-print-body h1{font-size:22px;margin:0 0 2px}#tax-print-body h2{font-size:14px;margin:24px 0 8px;color:#6b1d20;text-transform:uppercase;letter-spacing:.5px}
      #tax-print-body .sub{color:#666;margin:0 0 20px}#tax-print-body .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:0 0 8px}
      #tax-print-body .card{border:1px solid #e5e5e5;border-radius:10px;padding:12px}#tax-print-body .card .n{font-size:22px;font-weight:800}#tax-print-body .card .l{font-size:11px;color:#666;text-transform:uppercase}
      #tax-print-body table{width:100%;border-collapse:collapse;font-size:13px}#tax-print-body th,#tax-print-body td{text-align:left;padding:7px 8px;border-bottom:1px solid #eee}#tax-print-body th{color:#666;font-size:11px;text-transform:uppercase}
      #tax-print-body td:nth-child(n+2),#tax-print-body th:nth-child(n+2){text-align:right}#tax-print-body .foot{margin-top:28px;font-size:11px;color:#999}
      #tax-print-body .brand{display:flex;align-items:center;gap:12px;margin:0 0 14px}#tax-print-body .brand img{width:46px;height:46px;border-radius:11px}#tax-print-body .brand .bt{font-size:22px;font-weight:800;line-height:1.1}#tax-print-body .brand .bs{font-size:12px;color:#6b1d20;font-weight:600;text-transform:uppercase;letter-spacing:.6px}</style>
      <div id="tax-print-body">
      <div class="brand"><img src="/icon-512.png" alt="Stage Door"/><div><div class="bt">Stage Door</div><div class="bs">Tax Summary</div></div></div>
      <p class="sub">${label}</p>
      <div class="grid">
        <div class="card"><div class="n">${summary.count}</div><div class="l">Tours</div></div>
        <div class="card"><div class="n">${summary.days}</div><div class="l">Tour Days</div></div>
        <div class="card"><div class="n">$${Math.round(summary.tips).toLocaleString()}</div><div class="l">Total Tips</div></div>
        <div class="card"><div class="n">$${Math.round(summary.claimed).toLocaleString()}</div><div class="l">Claimed Tips (already taxed)</div></div>
      </div>
      <p class="sub">Average tip: ${summary.avg.toFixed(1)}% of retail</p>
      <h2>Total tips by payment type</h2><table><tbody>${typeRows || '<tr><td colspan="2">No tips in range</td></tr>'}</tbody></table>
      <h2>Monthly breakdown</h2><table><thead><tr><th>Month</th><th>Tours</th><th>Days</th><th>Total Tips</th><th>Claimed</th></tr></thead><tbody>${rows || '<tr><td colspan="5">No tours in range</td></tr>'}</tbody></table>
      <p class="foot">Generated by Stage Door on ${new Date().toLocaleDateString()}. Counts all tours with a date in range; tips shown only where a gratuity was recorded.</p>
      </div>`);
  };

  const chip = (key, label) => (
    <button
      key={key}
      onClick={() => setMode(key)}
      className={`rounded-full border px-3 py-1 text-xs font-600 transition-colors ${mode === key ? "border-[#9B2226] bg-[#9B2226]/20 text-[#E0575B]" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-zinc-100"}`}
      data-testid={`tax-range-${key}`}
    >{label}</button>
  );

  const hidden = financialPrivacy;
  const tipCell = (v) => (hidden ? <Lock className="inline h-3.5 w-3.5 text-zinc-600" /> : money(v));

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="tax-summary">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#9B2226]/20 text-[#E0575B]"><FileText className="h-4 w-4" /></div>
          <div>
            <h3 className="font-display text-base font-700 tracking-tight text-zinc-50">Year-in-Review & Tax Summary</h3>
            <p className="text-xs text-zinc-400">{range.label}</p>
          </div>
        </div>
        <Button onClick={buildReport} className="shrink-0 bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="tax-print-btn">
          <Printer className="mr-1.5 h-4 w-4" /> Print
        </Button>
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        {chip("year", "Tax year")}
        <button
          onClick={() => { setMode("year"); setYear(now.getFullYear() - 1); }}
          className={`rounded-full border px-3 py-1 text-xs font-600 transition-colors ${mode === "year" && year === now.getFullYear() - 1 ? "border-[#9B2226] bg-[#9B2226]/20 text-[#E0575B]" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:text-zinc-100"}`}
          data-testid="tax-range-lastyear"
        >Last year</button>
        {chip("ytd", "Year to date")}
        {chip("custom", "Custom")}
        {mode === "year" && (
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="ml-1 rounded-full border border-zinc-700 bg-zinc-900 px-3 py-1 text-xs text-zinc-100" data-testid="tax-year-select">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
      </div>
      {mode === "custom" && (
        <div className="mb-3 flex items-center gap-1.5" data-testid="tax-custom-range">
          <SingleDatePicker value={fromIso} onChange={setFromIso} testid="tax-from" placeholder="From" className="w-[150px]" />
          <span className="text-xs text-zinc-400">to</span>
          <SingleDatePicker value={toIso} onChange={setToIso} testid="tax-to" placeholder="To" className="w-[150px]" />
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { l: "Tours", v: summary.count, t: "tax-stat-tours" },
          { l: "Tour Days", v: summary.days, t: "tax-stat-days" },
          { l: "Total Tips", v: hidden ? null : money(summary.tips), t: "tax-stat-tips" },
          { l: "Claimed Tips", v: hidden ? null : money(summary.claimed), t: "tax-stat-claimed" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3" data-testid={s.t}>
            <div className="font-mono text-xl font-700 text-zinc-50">{s.v === null ? <Lock className="h-4 w-4 text-zinc-600" /> : s.v}</div>
            <div className="mt-0.5 text-[11px] uppercase tracking-wide text-zinc-400">{s.l}</div>
          </div>
        ))}
      </div>
      <p className="mb-4 text-xs text-zinc-400" data-testid="tax-avg">Avg tip: <span className="font-600 text-emerald-300">{summary.avg.toFixed(1)}%</span> · <span className="text-zinc-500">Claimed tips are already taxed</span></p>

      <div className="mb-4">
        <p className="mb-1.5 text-xs font-700 uppercase tracking-wider text-zinc-500">Total tips by type</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {["Cash", "Electronic", "Check", "Mix"].map((k) => (
            <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2" data-testid={`tax-type-${k.toLowerCase()}`}>
              <div className="text-sm font-600 text-zinc-100">{tipCell(summary.byType[k])}</div>
              <div className="text-[11px] text-zinc-500">{k}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-700 uppercase tracking-wider text-zinc-500">Monthly breakdown</p>
        {summary.monthRows.length === 0 ? (
          <p className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-sm text-zinc-500" data-testid="tax-empty">No tipped tours in this range.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950">
                <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-400">
                  <th className="px-3 py-2 font-600">Month</th>
                  <th className="px-3 py-2 text-right font-600">Tours</th>
                  <th className="px-3 py-2 text-right font-600">Tour Days</th>
                  <th className="px-3 py-2 text-right font-600">Total Tips</th>
                  <th className="px-3 py-2 text-right font-600">Claimed</th>
                </tr>
              </thead>
              <tbody>
                {summary.monthRows.map((r) => (
                  <tr key={`${r.y}-${r.m}`} className="border-t border-zinc-800/60" data-testid="tax-month-row">
                    <td className="px-3 py-2 text-zinc-200">{MONTHS[r.m]} {r.y}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{r.tours}</td>
                    <td className="px-3 py-2 text-right text-zinc-300" data-testid="tax-month-days">{r.days}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{tipCell(r.tips)}</td>
                    <td className="px-3 py-2 text-right font-600 text-emerald-300">{tipCell(r.claimed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {printHtml && (
        <div className="fixed inset-0 z-[120] overflow-auto bg-white" data-testid="tax-print-overlay" id="tax-print-area">
          <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-zinc-200 bg-white/95 px-4 py-3 backdrop-blur" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
            <button onClick={() => setPrintHtml(null)} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 px-3 py-1.5 text-sm font-600 text-zinc-700 hover:bg-zinc-100" data-testid="tax-print-close">
              <X className="h-4 w-4" /> Close
            </button>
            <button onClick={() => printOrSavePdf("tax-print-body", "Stage Door - Tax Summary.pdf")} className="inline-flex items-center gap-1.5 rounded-full bg-[#9B2226] px-4 py-1.5 text-sm font-700 text-white hover:bg-[#7A1A1E]" data-testid="tax-print-now">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
          <div className="p-6" dangerouslySetInnerHTML={{ __html: printHtml }} />
        </div>
      )}
    </div>
  );
};
